import { useMemo } from 'react';

import {
  BlotterDensity,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useConstant,
  usePersistedBlotterTable,
  useWLHomeCurrency,
  useWsBlotterTable,
  type Balance,
  type DateRangeFilter,
  type WebsocketRequest,
} from '@talos/kyoko';

import { map, pipe } from 'rxjs';

import { pick } from 'lodash-es';
import { useBalancesFilter } from '../../../../components/Filters';
import { useBalancesColumns, type PipedBalance } from './useBalancesColumns';

const generateRowID = (balance: Balance) => `${balance.MarketAccount}-${balance.Currency}`;

// Create a MarketAccount-Currency composite id for each datapoint received
const balanceCompositeIDPipe = pipe(
  map((result: { data: Balance[] }) => {
    return {
      ...result,
      data: result.data.map(balance => ({
        ...balance,
        RowID: generateRowID(balance),
      })),
    };
  })
);

interface BalancesBlotterWebsocketRequest extends WebsocketRequest {
  versionBy: string;
  EquivalentCurrency: string;
  ShowZeroBalances: boolean;
}

interface UseBalancesBlotterProps {
  renderEmpty: () => JSX.Element;
  showZeroBalances: boolean;
  colorful?: boolean;
}

const DEFAULT_SORT = '-Equivalent.AvailableAmount';

export const useBalancesBlotter = ({ colorful, renderEmpty, showZeroBalances }: UseBalancesBlotterProps) => {
  const { homeCurrency } = useWLHomeCurrency();
  const columns = useBalancesColumns(colorful);

  const persistedBlotterTable = usePersistedBlotterTable('balances');

  const filteredTrades = useBalancesFilter({ persistedBlotterTable, showZeroBalances });
  const { clientSideFilter: clientLocalFilter, filterBuilderProps } = filteredTrades;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const request = useMemo<BalancesBlotterWebsocketRequest>(
    () => ({
      name: 'Balance',
      tag: 'BLOTTER_BALANCES',
      versionBy: 'Revision',
      Throttle: '1s',
      EquivalentCurrency: homeCurrency,
      ShowZeroBalances: true,
      sort_by: DEFAULT_SORT,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredTrades.initialFilter)),
    }),
    [homeCurrency, filteredTrades.initialFilter]
  );

  const compositePipe = useConstant(pipe(balanceCompositeIDPipe));

  const blotterTable = useWsBlotterTable<BalancesBlotterWebsocketRequest, PipedBalance>({
    request,
    columns,
    persistence: persistedBlotterTable,
    rowID: 'RowID',
    clientLocalFilter,
    flashRows: ['add'],
    density: BlotterDensity.Comfortable,
    pipe: compositePipe,
    gridOptions: {
      noRowsOverlayComponent: renderEmpty,
      rowHeight: 40,
    },
  });

  return useMemo(() => ({ blotterTable, filterBuilderAccordion }), [blotterTable, filterBuilderAccordion]);
};

function onlyServerFilterKeys(filter: DateRangeFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['EndDate', 'StartDate']);
}
